import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { WhatsAppButton } from './components/WhatsAppButton';
import { TripEnquiryPopup } from './components/TripEnquiryPopup';

// Home is eagerly loaded (above-the-fold landing route - fastest LCP).
import { Home } from './pages/Home';

// All other public pages are lazy-loaded for route-based code splitting.
const Destinations = lazy(() => import('./pages/Destinations').then(m => ({ default: m.Destinations })));
const Packages = lazy(() => import('./pages/Packages').then(m => ({ default: m.Packages })));
const PackageDetail = lazy(() => import('./pages/PackageDetail').then(m => ({ default: m.PackageDetail })));
const AboutUs = lazy(() => import('./pages/AboutUs').then(m => ({ default: m.AboutUs })));
const ContactUs = lazy(() => import('./pages/ContactUs').then(m => ({ default: m.ContactUs })));
const TermsConditions = lazy(() => import('./pages/TermsConditions').then(m => ({ default: m.TermsConditions })));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy').then(m => ({ default: m.RefundPolicy })));
const FAQs = lazy(() => import('./pages/FAQs').then(m => ({ default: m.FAQs })));
const Blog = lazy(() => import('./pages/Blog').then(m => ({ default: m.Blog })));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({ default: m.BlogPost })));

// Admin imports - Lazy loaded to reduce initial bundle size
import { AuthProvider, useAuth } from './admin/context/AuthContext';
const AdminLayout = lazy(() => import('./admin/components/AdminLayout').then(m => ({ default: m.AdminLayout })));
const AdminLogin = lazy(() => import('./admin/pages/AdminLogin').then(m => ({ default: m.AdminLogin })));
const AdminDashboard = lazy(() => import('./admin/pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const BlogsManager = lazy(() => import('./admin/pages/BlogsManager').then(m => ({ default: m.BlogsManager })));
const PackagesManager = lazy(() => import('./admin/pages/PackagesManager').then(m => ({ default: m.PackagesManager })));
const DestinationsManager = lazy(() => import('./admin/pages/DestinationsManager').then(m => ({ default: m.DestinationsManager })));
const ReviewsManager = lazy(() => import('./admin/pages/ReviewsManager').then(m => ({ default: m.ReviewsManager })));
const FAQsManager = lazy(() => import('./admin/pages/FAQsManager').then(m => ({ default: m.FAQsManager })));
const SiteSettingsManager = lazy(() => import('./admin/pages/SiteSettingsManager'));
const HomepageManager = lazy(() => import('./admin/pages/HomepageManager'));
const AboutPageManager = lazy(() => import('./admin/pages/AboutPageManager'));
const ContactPageManager = lazy(() => import('./admin/pages/ContactPageManager'));
const PoliciesManager = lazy(() => import('./admin/pages/PoliciesManager'));
const ForgotPassword = lazy(() => import('./admin/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./admin/pages/ResetPassword'));
const ForgotUsername = lazy(() => import('./admin/pages/ForgotUsername'));
const ChangePassword = lazy(() => import('./admin/pages/ChangePassword'));
const ContactSubmissionsManager = lazy(() => import('./admin/pages/ContactSubmissionsManager'));

// Lightweight loader (no animation deps) for chunk loading.
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white" aria-label="Loading page">
    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-700"></div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { admin, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!admin) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <AdminLayout>{children}</AdminLayout>
    </Suspense>
  );
};

// Public Site Layout
const PublicLayout = ({ children }) => (
  <>
    <Header />
    <Suspense fallback={<PageLoader />}>{children}</Suspense>
    <Footer />
    <WhatsAppButton />
    <TripEnquiryPopup />
  </>
);

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
              {/* Public Routes - Home eager, rest lazy */}
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/destinations" element={<PublicLayout><Destinations /></PublicLayout>} />
              <Route path="/packages" element={<PublicLayout><Packages /></PublicLayout>} />
              <Route path="/packages/:slug" element={<PublicLayout><PackageDetail /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><AboutUs /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><ContactUs /></PublicLayout>} />
              <Route path="/terms" element={<PublicLayout><TermsConditions /></PublicLayout>} />
              <Route path="/privacy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
              <Route path="/refund" element={<PublicLayout><RefundPolicy /></PublicLayout>} />
              <Route path="/faqs" element={<PublicLayout><FAQs /></PublicLayout>} />
              <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
              <Route path="/blog/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />

              {/* Admin Routes - All lazy loaded */}
              <Route path="/admin/login" element={<Suspense fallback={<PageLoader />}><AdminLogin /></Suspense>} />
              <Route path="/admin/forgot-password" element={<Suspense fallback={<PageLoader />}><ForgotPassword /></Suspense>} />
              <Route path="/admin/reset-password" element={<Suspense fallback={<PageLoader />}><ResetPassword /></Suspense>} />
              <Route path="/admin/forgot-username" element={<Suspense fallback={<PageLoader />}><ForgotUsername /></Suspense>} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
              <Route path="/admin/site-settings" element={<ProtectedRoute><SiteSettingsManager /></ProtectedRoute>} />
              <Route path="/admin/homepage" element={<ProtectedRoute><HomepageManager /></ProtectedRoute>} />
              <Route path="/admin/about-page" element={<ProtectedRoute><AboutPageManager /></ProtectedRoute>} />
              <Route path="/admin/contact-page" element={<ProtectedRoute><ContactPageManager /></ProtectedRoute>} />
              <Route path="/admin/policies" element={<ProtectedRoute><PoliciesManager /></ProtectedRoute>} />
              <Route path="/admin/blogs" element={<ProtectedRoute><BlogsManager /></ProtectedRoute>} />
              <Route path="/admin/packages" element={<ProtectedRoute><PackagesManager /></ProtectedRoute>} />
              <Route path="/admin/destinations" element={<ProtectedRoute><DestinationsManager /></ProtectedRoute>} />
              <Route path="/admin/reviews" element={<ProtectedRoute><ReviewsManager /></ProtectedRoute>} />
              <Route path="/admin/faqs" element={<ProtectedRoute><FAQsManager /></ProtectedRoute>} />
              <Route path="/admin/contact-submissions" element={<ProtectedRoute><ContactSubmissionsManager /></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
